/**
 * Lazy-loads the Reviews.io Polaris widget when a [data-product-reviews-widget]
 * element scrolls into view. Loads the build.js once per page, then initializes
 * each widget against the per-block config dropped on window.__prwConfigs.
 *
 * Shared helpers (rating extractor, /5 injection, count <strong> wrap, cleanup)
 * komen uit assets/reviews-widgets-shared.js via window.RWS.
 */
(function () {
  'use strict';

  var BUILD_SCRIPT_SRC = 'https://widget.reviews.io/polaris/build.js';
  var ROOT_MARGIN = '400px 0px';

  var buildScriptPromise = null;

  function loadBuildScript() {
    if (buildScriptPromise) return buildScriptPromise;

    buildScriptPromise = new Promise(function (resolve, reject) {
      if (typeof window.ReviewsWidget === 'function') {
        resolve();
        return;
      }
      var existing = document.querySelector('script[data-prw-build]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = BUILD_SCRIPT_SRC;
      script.async = true;
      script.setAttribute('data-prw-build', '');
      script.onload = function () { resolve(); };
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return buildScriptPromise;
  }

  function initWidget(el) {
    if (el.dataset.prwInitialized === 'true') return;
    el.dataset.prwInitialized = 'true';

    var configId = el.dataset.configId;
    var mountId = el.dataset.mountId;
    var config = window.__prwConfigs && window.__prwConfigs[configId];

    if (!config || !mountId) {
      if (window.__cnDebug) console.warn('[product-reviews-widget] missing config or mountId', configId);
      return;
    }

    loadBuildScript()
      .then(function () {
        if (typeof window.ReviewsWidget !== 'function') {
          throw new Error('ReviewsWidget global missing after build.js load');
        }
        new window.ReviewsWidget('#' + mountId, config);
        el.classList.add('product-reviews-widget--loaded');
        attachEnhancer(el);
      })
      .catch(function (err) {
        if (window.__cnDebug) console.error('[product-reviews-widget] init failed', err);
        el.classList.add('product-reviews-widget--error');
      });
  }

  /**
   * Per-review enhancement — gebruikt window.RWS shared helpers.
   * - Inject "X/5" naast star-group binnen .item__review
   * - Voeg "/5" toe aan summary header rating
   * - Wrap count getal in <strong>
   *
   * Cleanup: MutationObserver disconnect bij section-unload.
   */
  function attachEnhancer(el) {
    if (!window.RWS) {
      // shared.js niet geladen → silently skip enhancement
      return;
    }

    function enhanceAll(scope) {
      var reviews = scope.querySelectorAll('.item__review');
      Array.prototype.forEach.call(reviews, function (reviewEl) {
        var starGroup = reviewEl.querySelector('.R-RatingStars');
        if (starGroup) window.RWS.injectRating(starGroup, 'prw-rating-num');
      });
      fixSummaryHeader(scope);
      injectHeaderBranding(scope);
    }

    // Injecteer de brand-elementen in de Reviews.io summary-header:
    // UITSTEKEND-pill bovenaan + Reviews.io-logo onder de count. Guards
    // voorkomen dubbele injectie bij re-renders (MutationObserver).
    function injectHeaderBranding(scope) {
      var inner = scope.querySelector('.ElementsWidget__header .header__inner');
      if (!inner) return;
      // Lees label + alt text uit data-attrs op de widget-wrapper. Liquid
      // set die uit de cn_reviews translation-keys (NL/EN/DE).
      var widget = scope.matches('[data-product-reviews-widget]') ? scope : scope.closest('[data-product-reviews-widget]');
      var pillText = (widget && widget.dataset.cnPillText) || 'EXCELLENT';
      var logoAlt = (widget && widget.dataset.cnLogoAlt) || 'Reviews.io';
      if (!inner.querySelector('.cn-prw-pill')) {
        var pill = document.createElement('span');
        pill.className = 'cn-rb__pill cn-prw-pill';
        pill.textContent = pillText;
        inner.insertBefore(pill, inner.firstChild);
      }
      // Gebruik het geüploade logo (Shopify CDN, laadt betrouwbaar). Geen
      // geüpload logo → geen injectie (de officiële externe SVG laadt niet
      // als <img>, dus die slaan we over).
      if (window.__cnReviewsLogoUrl && !inner.querySelector('.cn-prw-logo')) {
        var logo = document.createElement('img');
        logo.className = 'cn-prw-logo';
        logo.src = window.__cnReviewsLogoUrl;
        logo.alt = logoAlt;
        logo.loading = 'lazy';
        // Inline style met !important — Reviews.io's eigen CSS heeft een
        // 5-class specificity selector `[class*="Attributes"]:empty` die
        // display:none !important zet op leeg-class elementen in de header.
        // Onze externe .cn-prw-logo rule verliest van die specificity.
        // Inline !important wint van alles.
        logo.style.setProperty('display', 'block', 'important');
        logo.style.setProperty('width', '150px', 'important');
        logo.style.setProperty('height', 'auto', 'important');
        // Margin via CSS regelen (product-reviews-widget.css) zodat
        // sectie-specifieke styling de positie kan tunen zonder de
        // inline !important te hoeven overrulen.
        // Direct in header__inner (stapelt verticaal, net als de pill) → komt
        // onder de rating/count. Niet in de count-rij zelf (die drukt 'm weg).
        inner.appendChild(logo);
      }
    }

    function fixSummaryHeader(scope) {
      var inner = scope.querySelector('.ElementsWidget__header .header__inner');
      if (!inner) return;

      // Stap 1: rating "4.8" → "4.8/5" — ALLEEN binnen .R-TextHeading--inline
      // (het grote rating-cijfer), niet binnen .R-TextBody (count text)
      var ratingScopes = inner.querySelectorAll('.R-TextHeading--inline');
      Array.prototype.forEach.call(ratingScopes, function (scope) {
        var allElements = scope.querySelectorAll('span, div, p, strong, b');
        // Include scope zelf (kan een leaf-element zijn als textContent direct erin)
        var candidates = scope.children.length > 0 ? allElements : [scope];
        Array.prototype.forEach.call(candidates, function (s) {
          if (s.dataset.prwSuffixed === '1') return;
          if (s.children.length > 0) return;
          var text = (s.textContent || '').trim();
          if (/^[\d]+(\.[\d]+)?$/.test(text)) {
            var num = parseFloat(text);
            if (num >= 1 && num <= 5) {
              s.dataset.prwSuffixed = '1';
              s.textContent = text + '/5';
            }
          }
        });
      });

      // Stap 2: wrap count in <strong> via shared helper
      var countTextEls = inner.querySelectorAll('.R-TextBody--xs');
      Array.prototype.forEach.call(countTextEls, function (el) {
        window.RWS.wrapCountWithStrong(el);
      });
    }

    // Initial pass — Polaris async rendert (~300-800ms typisch)
    var initialTimer = setTimeout(function () { enhanceAll(el); }, 500);

    // MutationObserver voor pagination/filter re-renders
    var mo = new MutationObserver(function () { enhanceAll(el); });
    mo.observe(el, { childList: true, subtree: true });

    // Cleanup op shopify:section:unload (Theme Editor)
    window.RWS.registerCleanup(el, function () {
      clearTimeout(initialTimer);
      mo.disconnect();
    });
  }

  function observe(el) {
    // Widgets binnen de reviews-modal mogen NIET op page-load initialiseren.
    // De modal is verborgen via visibility/opacity (houdt layout), dus de
    // IntersectionObserver zou 'm tóch zien en de store-wide config (incl.
    // bedrijfsreviews) mounten — die racet met de PDP product-widget via
    // Polaris' globale state en toont soms de "algemene widget". Daarom
    // stellen we de init uit tot de modal daadwerkelijk opent (.is-open).
    var modal = el.closest('.reviews-modal');
    if (modal && !modal.classList.contains('is-open')) {
      var modalMo = new MutationObserver(function () {
        if (modal.classList.contains('is-open')) {
          modalMo.disconnect();
          initWidget(el);
        }
      });
      modalMo.observe(modal, { attributes: true, attributeFilter: ['class'] });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      initWidget(el);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.disconnect();
          initWidget(el);
        }
      });
    }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });
    io.observe(el);
  }

  function bootAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-product-reviews-widget]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.dataset.prwObserved === 'true') return;
      el.dataset.prwObserved = 'true';
      observe(el);
    });
  }

  /**
   * Eager-init: when the user clicks the inline rating anchor on the
   * PDP (which scrolls to #reviews), trigger the widget init the
   * moment they click instead of waiting for the IntersectionObserver
   * to fire after the scroll lands. This overlaps the build.js
   * network fetch with the smooth-scroll animation, so by the time
   * the user reaches the section the widget is usually ready.
   */
  function eagerInitOnAnchorClick() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-prw-anchor-trigger]');
      if (!trigger) return;
      // Kick off init on every matching widget on the page (in practice
      // one). Don't preventDefault — the browser still handles the
      // hash navigation + smooth scroll.
      var widgets = document.querySelectorAll('[data-product-reviews-widget]');
      Array.prototype.forEach.call(widgets, function (el) {
        // Nooit de modal-widget eager-initen vanaf de PDP-rating-anchor: die
        // heeft een store-wide config die de product-widget zou overschrijven.
        if (el.closest('.reviews-modal')) return;
        if (el.dataset.prwInitialized === 'true') return;
        initWidget(el);
      });
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bootAll(document);
      eagerInitOnAnchorClick();
    });
  } else {
    bootAll(document);
    eagerInitOnAnchorClick();
  }

  document.addEventListener('shopify:section:load', function (e) {
    bootAll(e.target || document);
  });
  document.addEventListener('shopify:section:select', function (e) {
    bootAll(e.target || document);
  });
})();
